import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useCustomer } from "@/lib/customer";
import { CustomerAuthForm } from "@/components/CustomerAuthForm";
import { money } from "@/lib/currency";
import { RestaurantAddress } from "@/components/RestaurantAddress";
import { OpenStatus } from "@/components/OpenStatus";
import { ReceiptEmail } from "@/components/ReceiptEmail";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { LoyaltyModal } from "@/components/LoyaltyModal";
import { OrderTracker } from "@/components/OrderTracker";
import { Plus, Minus, ShoppingBag, X, ChefHat, Loader2, CheckCircle2, Clock, Search, Gift, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { LanguagePicker } from "@/components/LanguagePicker";
import { AddonSheet } from "@/components/AddonSheet";
import { LineAddons } from "@/components/LineAddons";
import { addonSum, cartKey } from "@/lib/addons";

const DIET_STYLE = {
  Vegan: "bg-emerald-100 text-emerald-700",
  Vegetarian: "bg-green-100 text-green-700",
  "Gluten-Free": "bg-amber-100 text-amber-700",
  Spicy: "bg-red-100 text-red-700",
  "Chef Special": "bg-orange-100 text-orange-700",
};

export default function CustomerMenu() {
  const { restaurantId } = useParams();
  const [params] = useSearchParams();
  const table = params.get("table") || "1";
  const { t } = useT();

  const [restaurant, setRestaurant] = useState(null);
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [cart, setCart] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [note, setNote] = useState("");
  const [activeCat, setActiveCat] = useState("__all");
  const [query, setQuery] = useState("");
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [loyaltyOpen, setLoyaltyOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [addonItem, setAddonItem] = useState(null);
  const { card, setCard } = useCustomer(restaurantId);

  useEffect(() => {
    Promise.all([api.get(`/restaurants/${restaurantId}`), api.get(`/restaurants/${restaurantId}/menu`)])
      .then(([r, m]) => {
        setRestaurant(r.data);
        setMenu(m.data);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [restaurantId]);

  useEffect(() => {
    api.get(`/restaurants/${restaurantId}/loyalty`).then((r) => setLoyalty(r.data)).catch(() => {});
  }, [restaurantId]);

  useEffect(() => {
    const t = setInterval(() => {
      api.get(`/restaurants/${restaurantId}`).then((r) => setRestaurant(r.data)).catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, [restaurantId]);

  const closed = restaurant?.open_status?.open === false;

  const accent = restaurant?.primary_color || "#D9531E";
  const cur = restaurant?.currency_symbol || "$";
  const categories = useMemo(() => ["__all", ...Array.from(new Set(menu.map((m) => m.category)))], [menu]);
  const filtered = menu.filter(
    (m) => (activeCat === "__all" || m.category === activeCat) && m.name.toLowerCase().includes(query.toLowerCase())
  );

  const hasAddons = (item) => item.addons?.length > 0;
  const add = (item, addons = []) => {
    const key = cartKey(item.id, addons);
    setCart((c) => ({ ...c, [key]: { key, item, addons, qty: (c[key]?.qty || 0) + 1 } }));
    toast.success(t("added", { name: item.name }), { duration: 1200 });
  };
  const inCart = (id) => Object.values(cart).filter((c) => c.item.id === id).reduce((s, c) => s + c.qty, 0);
  const dec = (id) =>
    setCart((c) => {
      const qty = (c[id]?.qty || 0) - 1;
      const next = { ...c };
      if (qty <= 0) delete next[id];
      else next[id] = { ...next[id], qty };
      return next;
    });

  const cartItems = Object.values(cart);
  const totalQty = cartItems.reduce((s, c) => s + c.qty, 0);
  const totalPrice = cartItems.reduce((s, c) => s + c.qty * (c.item.price + addonSum(c.addons)), 0);

  const placeOrder = async () => {
    setPlacing(true);
    try {
      const { data } = await api.post(`/restaurants/${restaurantId}/orders`, {
        table_number: parseInt(table),
        note,
        items: cartItems.map((c) => ({ item_id: c.item.id, name: c.item.name, price: c.item.price, qty: c.qty, addon_ids: c.addons.map((a) => a.id) })),
      });
      setPlaced(data);
      window.scrollTo(0, 0);
      setCart({});
      setNote("");
      setCartOpen(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.closed) toast.error(`${t("closedNow")}${detail.next_open_time ? ` ${t("weOpen", { day: t(detail.next_open_day), time: detail.next_open_time })}` : ""}`);
      else if (detail?.sold_out_addons) toast.error(t("soldOutMsg", { names: detail.sold_out_addons.map((a) => a.name).join(", ") }));
      else if (detail?.sold_out) toast.error(t("soldOutMsg", { names: cartItems.filter((c) => detail.sold_out.includes(c.item.id)).map((c) => c.item.name).join(", ") }));
      else toast.error(formatApiErrorDetail(detail) || t("orderError"));
      if (detail?.closed) {
        setCartOpen(false);
        api.get(`/restaurants/${restaurantId}`).then((r) => setRestaurant(r.data)).catch(() => {});
      }
      if (detail?.sold_out) {
        setCart((c) => Object.fromEntries(Object.entries(c).filter(([, v]) => !detail.sold_out.includes(v.item.id))));
        api.get(`/restaurants/${restaurantId}/menu`).then((m) => setMenu(m.data)).catch(() => {});
      }
      if (detail?.sold_out_addons) {
        const bad = detail.sold_out_addons.map((a) => a.addon_id);
        setCart((c) => Object.fromEntries(Object.entries(c).filter(([, v]) => !v.addons.some((a) => bad.includes(a.id)))));
        api.get(`/restaurants/${restaurantId}/menu`).then((m) => setMenu(m.data)).catch(() => {});
      }
    } finally {
      setPlacing(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAF7F2]">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: accent }} />
      </div>
    );

  if (notFound)
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAF7F2] text-center px-6">
        <div>
          <ChefHat className="h-12 w-12 mx-auto text-[#D9531E]" />
          <h1 className="font-heading text-2xl font-bold mt-4 text-[#1F1D1B]">{t("menuNotFound")}</h1>
          <p className="text-[#6B6560] mt-2">{t("menuNotFoundDesc")}</p>
        </div>
      </div>
    );

  if (placed)
    return (
      <div className="max-w-md mx-auto min-h-screen bg-[#FAF7F2] px-5 py-8 fade-up">
        <div className="flex justify-end mb-2"><LanguagePicker /></div>
        <div className="flex flex-col items-center text-center">
          <div className="h-16 w-16 rounded-full grid place-items-center" style={{ backgroundColor: `${accent}20` }}>
            <CheckCircle2 className="h-9 w-9" style={{ color: accent }} />
          </div>
          <h1 className="font-heading text-2xl font-bold mt-4 text-[#1F1D1B]">{t("orderPlaced")}</h1>
          <p className="text-[#6B6560] mt-1 text-sm">{t("tableTrack", { table })}</p>
          {card && <p data-testid="order-saved-note" className="text-xs mt-1" style={{ color: accent }}>{t("savedHistory")}</p>}
        </div>
        <div className="mt-6">
          <OrderTracker orderId={placed.id} accent={accent} currency={cur} initial={placed} />
        </div>
        <RestaurantAddress restaurant={restaurant} accent={accent} className="mt-4" />
        <div className="mt-4"><ReceiptEmail orderId={placed.id} accent={accent} defaultEmail={card?.member?.email || ""} onSent={(email) => card && setCard({ ...card, member: { ...card.member, email } })} /></div>
        <button
          data-testid="copy-track-link-button"
          onClick={() => {
            navigator.clipboard?.writeText(`${window.location.origin}/track/${placed.id}`);
            toast.success(t("trackCopied"));
          }}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 border border-[#E6E1DA] bg-white rounded-full py-3 text-sm font-medium text-[#1F1D1B]"
        >
          {t("saveTrackLink")}
        </button>
        <button data-testid="order-again-button" onClick={() => { setPlaced(null); window.scrollTo(0, 0); }} className="mt-2 w-full rounded-full py-3.5 font-semibold text-white" style={{ backgroundColor: accent }}>
          {t("orderMore")}
        </button>
      </div>
    );

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#FAF7F2] relative pb-28 shadow-2xl">
      {/* Cover */}
      <div className="relative h-44">
        <img
          src={restaurant.cover_path ? fileUrl(restaurant.cover_path) : "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"}
          alt="cover"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="absolute top-3 left-3"><LanguagePicker /></div>
        <div className="absolute top-3 right-3">
          {card ? (
            <Link to={`/account/${restaurantId}?table=${table}`} data-testid="my-account-button" className="inline-flex items-center gap-1.5 bg-white/90 backdrop-blur text-[#1F1D1B] text-sm font-semibold rounded-full px-3.5 py-1.5 shadow">
              <UserRound className="h-4 w-4" style={{ color: accent }} /> {t("myAccount")}
            </Link>
          ) : (
            <button data-testid="customer-login-button" onClick={() => setAuthOpen(true)} className="inline-flex items-center gap-1.5 bg-white/90 backdrop-blur text-[#1F1D1B] text-sm font-semibold rounded-full px-3.5 py-1.5 shadow transition-transform active:scale-95">
              <UserRound className="h-4 w-4" style={{ color: accent }} /> {t("logIn")}
            </button>
          )}
        </div>
        <div className="absolute bottom-4 left-4 right-4 flex items-end gap-3">
          <div className="h-14 w-14 rounded-2xl overflow-hidden bg-white grid place-items-center shrink-0 shadow-lg">
            {restaurant.logo_path ? <img src={fileUrl(restaurant.logo_path)} alt="logo" className="h-full w-full object-cover" /> : <ChefHat className="h-7 w-7" style={{ color: accent }} />}
          </div>
          <div className="text-white pb-1">
            <h1 className="font-heading text-2xl font-bold leading-tight">{restaurant.name}</h1>
            <p className="text-xs text-white/80">{restaurant.description}</p>
          </div>
        </div>
      </div>

      {/* Table badge */}
      <div className="px-4 pt-4">
        {restaurant.open_status?.configured && <div className="mb-3"><OpenStatus status={restaurant.open_status} /></div>}
        {closed && (
          <p data-testid="closed-banner" className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3">
            {t("closedBanner")}
          </p>
        )}
        <RestaurantAddress restaurant={restaurant} accent={accent} className="mb-3" />
        <span data-testid="table-badge" className="inline-block text-xs font-medium uppercase tracking-wider px-3 py-1 rounded-full text-white" style={{ backgroundColor: accent }}>
          {t("table", { n: table })}
        </span>
      </div>

      {/* Loyalty banner */}
      {loyalty?.active && (
        <div className="px-4 pt-3">
          <button
            data-testid="loyalty-banner"
            onClick={() => setLoyaltyOpen(true)}
            className="w-full flex items-center gap-3 rounded-2xl border border-dashed p-3.5 text-left transition-transform active:scale-[0.99]"
            style={{ borderColor: accent, backgroundColor: `${accent}10` }}
          >
            <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0" style={{ backgroundColor: accent }}>
              <Gift className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="font-sub font-semibold text-sm text-[#1F1D1B]">{loyalty.program_name}</p>
              <p className="text-xs text-[#6B6560] truncate">{t("collectStamps", { reward: loyalty.reward_description })}</p>
            </div>
          </button>
        </div>
      )}

      {authOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center" onClick={() => setAuthOpen(false)}>
          <div data-testid="customer-auth-modal" className="w-full max-w-md bg-white rounded-t-3xl p-5 pb-safe max-h-[90dvh] overflow-y-auto fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-[#1F1D1B]">{t("yourAccount")}</h2>
              <button data-testid="close-customer-auth" onClick={() => setAuthOpen(false)} className="text-[#6B6560]"><X className="h-6 w-6" /></button>
            </div>
            <p className="text-sm text-[#6B6560] mb-4">{t("loginPrompt")}</p>
            <CustomerAuthForm restaurantId={restaurantId} accent={accent} onSuccess={(d) => { setCard(d); setAuthOpen(false); toast.success(d.member.name ? t("welcomeName", { name: d.member.name }) : t("welcome")); }} />
          </div>
        </div>
      )}

      {addonItem && <AddonSheet item={addonItem} cur={cur} accent={accent} onClose={() => setAddonItem(null)} onAdd={(item, addons) => { add(item, addons); setAddonItem(null); }} />}

      <LoyaltyModal open={loyaltyOpen} onClose={() => setLoyaltyOpen(false)} restaurantId={restaurantId} program={loyalty} accent={accent} />

      {/* Search */}
      <div className="px-4 pt-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A39E96]" />
          <input
            data-testid="menu-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("searchDishes")}
            className="w-full bg-white border border-[#E6E1DA] rounded-xl pl-9 pr-4 py-2.5 text-sm text-[#1F1D1B] focus:outline-none focus:border-[#D9531E]"
          />
        </div>
      </div>

      {/* Category nav */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar py-3 px-4 sticky top-0 z-30 bg-[#FAF7F2]/95 backdrop-blur">
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`category-${c === "__all" ? "All" : c}`}
            onClick={() => setActiveCat(c)}
            className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${activeCat === c ? "text-white border-transparent" : "bg-white text-[#6B6560] border-[#E6E1DA]"}`}
            style={activeCat === c ? { backgroundColor: accent } : {}}
          >
            {c === "__all" ? t("all") : c}
          </button>
        ))}
      </div>

      {/* Menu list */}
      <div className="px-4 space-y-3">
        {filtered.length === 0 && <p className="text-center text-[#6B6560] py-10 text-sm">{t("noDishes")}</p>}
        {filtered.map((item, i) => (
          <div key={item.id} data-testid={`customer-item-${item.id}`} className={`bg-white rounded-2xl border border-[#E6E1DA] p-3 flex gap-3 fade-up ${item.available ? "" : "opacity-60"}`} style={{ animationDelay: `${i * 0.03}s` }}>
            {item.image_path && <img src={fileUrl(item.image_path)} alt={item.name} loading="lazy" className={`h-24 w-24 rounded-xl object-cover shrink-0 ${item.available ? "" : "grayscale"}`} />}
            <div className="flex-1 min-w-0">
              <h3 className="font-sub font-semibold text-[#1F1D1B]">{item.name}</h3>
              {item.description && <p className="text-xs text-[#6B6560] mt-0.5 line-clamp-2">{item.description}</p>}
              <div className="flex flex-wrap gap-1 mt-1.5">
                {item.dietary?.map((d) => (
                  <span key={d} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${DIET_STYLE[d] || "bg-gray-100 text-gray-600"}`}>{d}</span>
                ))}
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="font-mono-j font-bold text-[#1F1D1B]">{money(item.price, cur)}</span>
                {!item.available ? (
                  <span data-testid={`sold-out-${item.id}`} className="text-xs font-semibold uppercase tracking-wider text-red-600 bg-red-50 border border-red-200 rounded-full px-3 py-1">{t("soldOut")}</span>
                ) : closed ? null : hasAddons(item) ? (
                  <button data-testid={`add-${item.id}`} onClick={() => setAddonItem(item)} className="inline-flex items-center gap-1 text-white text-sm font-semibold rounded-full px-4 py-2 min-h-[36px]" style={{ backgroundColor: accent }}>
                    <Plus className="h-4 w-4" /> {t("add")}{inCart(item.id) > 0 && <span data-testid={`in-cart-count-${item.id}`} className="ml-1 bg-white/25 rounded-full px-1.5 text-xs">{inCart(item.id)}</span>}
                  </button>
                ) : cart[item.id] ? (
                  <div className="flex items-center gap-2 rounded-full px-0.5 py-0.5" style={{ backgroundColor: accent }}>
                    <button data-testid={`dec-${item.id}`} onClick={() => dec(item.id)} className="h-9 w-9 grid place-items-center text-white"><Minus className="h-4 w-4" /></button>
                    <span className="text-white text-sm font-bold w-4 text-center">{cart[item.id].qty}</span>
                    <button data-testid={`inc-${item.id}`} onClick={() => add(item)} className="h-9 w-9 grid place-items-center text-white"><Plus className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <button data-testid={`add-${item.id}`} onClick={() => add(item)} className="inline-flex items-center gap-1 text-white text-sm font-semibold rounded-full px-4 py-2 min-h-[36px]" style={{ backgroundColor: accent }}>
                    <Plus className="h-4 w-4" /> {t("add")}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Floating cart bar */}
      {totalQty > 0 && !cartOpen && !closed && !addonItem && (
        <button
          data-testid="view-cart-button"
          onClick={() => setCartOpen(true)}
          className="fixed bottom-safe left-1/2 -translate-x-1/2 w-[92%] max-w-md text-white p-4 rounded-2xl shadow-2xl flex items-center justify-between z-50 transition-transform active:scale-[0.98]"
          style={{ backgroundColor: accent }}
        >
          <span className="flex items-center gap-2 font-semibold"><ShoppingBag className="h-5 w-5" /> {t(totalQty > 1 ? "itemMany" : "itemOne", { n: totalQty })}</span>
          <span className="font-mono-j font-bold">{t("viewCart")} · {money(totalPrice, cur)}</span>
        </button>
      )}

      {/* Cart drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center" onClick={() => setCartOpen(false)}>
          <div className="w-full max-w-md bg-[#FAF7F2] rounded-t-3xl p-5 pb-safe max-h-[85dvh] overflow-y-auto no-scrollbar fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl font-bold text-[#1F1D1B]">{t("yourOrder")}</h2>
              <button data-testid="close-cart-button" onClick={() => setCartOpen(false)} className="text-[#6B6560]"><X className="h-6 w-6" /></button>
            </div>
            <div className="space-y-3">
              {cartItems.map((c) => (
                <div key={c.key} data-testid={`cart-line-${c.key}`} className="flex items-center justify-between gap-2 bg-white rounded-xl p-3 border border-[#E6E1DA]">
                  <div className="min-w-0">
                    <p className="font-sub font-semibold text-sm text-[#1F1D1B] truncate">{c.item.name}</p>
                    <LineAddons addons={c.addons} cur={cur} />
                    <p className="text-xs text-[#6B6560] font-mono-j">{money(c.item.price + addonSum(c.addons), cur)}</p>
                  </div>
                  <div className="flex items-center gap-2 rounded-full px-0.5 py-0.5 shrink-0" style={{ backgroundColor: accent }}>
                    <button data-testid={`cart-dec-${c.key}`} onClick={() => dec(c.key)} className="h-9 w-9 grid place-items-center text-white"><Minus className="h-4 w-4" /></button>
                    <span className="text-white text-sm font-bold w-4 text-center">{c.qty}</span>
                    <button data-testid={`cart-inc-${c.key}`} onClick={() => add(c.item, c.addons)} className="h-9 w-9 grid place-items-center text-white"><Plus className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
            <textarea
              data-testid="order-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("notePlaceholder")}
              rows={2}
              className="w-full mt-4 bg-white border border-[#E6E1DA] rounded-xl px-4 py-3 text-sm text-[#1F1D1B] focus:outline-none focus:border-[#D9531E] resize-none"
            />
            <div className="flex justify-between items-center mt-4 text-[#1F1D1B]">
              <span className="text-[#6B6560]">{t("totalCounter")}</span>
              <span className="font-mono-j font-bold text-xl">{money(totalPrice, cur)}</span>
            </div>
            <button
              data-testid="place-order-button"
              onClick={placeOrder}
              disabled={placing}
              className="w-full mt-4 rounded-full py-4 font-bold text-white flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ backgroundColor: accent }}
            >
              {placing && <Loader2 className="h-5 w-5 animate-spin" />} {t("placeOrder")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
